import Bugsnag, { Event } from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import { CONFIG } from '@/constants/config';
import { isJestRunning } from '@/utils/helpers';

// Fields we read from event.originalError (Bugsnag types it as any).
type OriginalError =
  | string
  | {
      message?: unknown;
      name?: unknown;
      status?: { category?: unknown };
    }
  | undefined;

const IGNORED_ERRORS: { message: string | RegExp }[] = [
  { message: "Cannot read properties of null (reading 'setItem')" },
  { message: "Cannot read property 'setItem' of null" },
  // zustand persist when localStorage is unavailable (private mode / webview)
  { message: /getItem/ },
  // Transient network failures (offline, aborted request, iOS Safari backgrounding)
  { message: /failed to fetch/i },
  { message: /\bload failed\b/i },
  {
    message:
      /PNTimeoutCategory|PNNetworkIssuesCategory|PNAccessDeniedCategory|PNBadRequestCategory/,
  },
  // Truncated JS chunk (dynamic import / network / cache) — not a source syntax bug
  { message: 'Unexpected end of input' },
  // RangeError from browser extensions / third-party scripts; not actionable from app code
  { message: /Maximum call stack size exceeded/i },
];

const NOOP_BUGSNAG_API_KEY = 'DO_NOT_SEND';

const STAGING_DOMAINS = [
  '.azstaging.net',
  '.azstage.in',
  '.digit-az.app',
  '.vercel.app',
];

const isBrowser = typeof window !== 'undefined';
const apiKey = CONFIG.bugsnagApiKey;

const noopBugsnag = {
  notify: () => undefined,
  isStarted: () => false,
};

export function getErrorMessages(event: Event): string[] {
  const original = event.originalError as OriginalError;

  const fromOriginal =
    typeof original === 'string'
      ? [original]
      : [
          typeof original?.message === 'string' ? original.message : '',
          // PubNub notify path may put the category only on name / status
          typeof original?.name === 'string' ? original.name : '',
          typeof original?.status?.category === 'string'
            ? original.status.category
            : '',
        ];

  return [
    ...(event.errors || []).map(e => e.errorMessage || ''),
    ...fromOriginal,
  ].filter(Boolean);
}

export function shouldIgnoreError(event: Event): boolean {
  const errorMessages = getErrorMessages(event);

  return IGNORED_ERRORS.some(({ message }) =>
    errorMessages.some(msg => {
      if (message instanceof RegExp) {
        return message.test(msg);
      }

      return msg === message;
    })
  );
}

function matchesStagingDomain(url?: string): boolean {
  return STAGING_DOMAINS.some(domain => url?.includes(domain));
}

function getReleaseStage(): string {
  if (isBrowser) {
    const { hostname } = window.location;

    if (hostname.match(/^(?:.+\.)?tap\.az$/)) {
      return 'production';
    }

    if (matchesStagingDomain(hostname)) {
      return process.env.VERCEL_GITHUB_BRANCH || 'staging';
    }

    if (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '0.0.0.0'
    ) {
      return 'local';
    }

    return 'local';
  }

  if (process.env.VERCEL_ENV === 'production') {
    return 'production';
  }

  if (matchesStagingDomain(process.env.VERCEL_URL)) {
    return (
      process.env.VERCEL_GITHUB_BRANCH ||
      process.env.VERCEL_GIT_COMMIT_REF ||
      'staging'
    );
  }

  if (process.env.VERCEL_GITHUB_BRANCH) {
    return process.env.VERCEL_GITHUB_BRANCH;
  }

  return 'local';
}

function isLocalLogging(): boolean {
  if (process.env.E2E_TEST_MODE === 'true') {
    return false;
  }

  return getReleaseStage() === 'local';
}

function getLogContext(): string {
  return isBrowser ? 'Browser' : 'Server';
}

function getEventLogPayload(event: Event) {
  return {
    errorClass: event.errors?.[0]?.errorClass,
    error: event.errors?.[0]?.errorMessage,
    context: event.context,
  };
}

function createConfig() {
  return {
    apiKey,
    enabledReleaseStages: null,
    autoDetectErrors: true,
    enabledErrorTypes: {
      unhandledExceptions: true,
      unhandledRejections: true,
    },
    plugins: isBrowser ? [new BugsnagPluginReact()] : [],
    onError(event: Event) {
      const logContext = getLogContext();

      if (shouldIgnoreError(event)) {
        if (isLocalLogging()) {
          console.log(
            `[Bugsnag ${logContext}] Error ignored (not sent):`,
            getEventLogPayload(event)
          );
        }
        return false;
      }

      if (apiKey === NOOP_BUGSNAG_API_KEY) {
        return false;
      }

      // eslint-disable-next-line no-param-reassign -- Bugsnag event mutation API
      event.app.releaseStage = getReleaseStage();

      if (isLocalLogging()) {
        console.log(`[Bugsnag ${logContext}] Sending error to Bugsnag:`, {
          releaseStage: event.app.releaseStage,
          ...getEventLogPayload(event),
        });
      }

      return true;
    },
  };
}

export function startBugsnag() {
  if (isJestRunning() || apiKey === NOOP_BUGSNAG_API_KEY) {
    return noopBugsnag;
  }

  if (!Bugsnag.isStarted()) {
    return Bugsnag.start(createConfig());
  }

  return Bugsnag;
}

export const bugsnag = startBugsnag();

export default Bugsnag;
